import React, { useState } from 'react';
import {useLoginBackgroundStyle} from '../utilities/loginBackground';
import {GenreIcon} from '../utilities/genreIcons';
import { useParams, useNavigate } from 'react-router-dom';
import { RiLockPasswordFill } from 'react-icons/ri';
import { MdAlternateEmail } from 'react-icons/md';
import {useBrandLogo} from '../utilities/genreIcons';
import VrPortalLogo from './VrPortalLogo';
import Swal from 'sweetalert2';
import './LoginForm.css';

const TAB_LOGIN = 'login';
const TAB_SIGNUP = 'signup';

function JoinPage() {
    const loginBgStyle = useLoginBackgroundStyle();
    const ddLogo = useBrandLogo();
    const { inviteCode } = useParams();
    const navigate = useNavigate();

    const [tab, setTab] = useState(TAB_LOGIN);
    const [submitting, setSubmitting] = useState(false);

    // Login fields
    const [loginUsername, setLoginUsername] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // Signup fields
    const [signupEmail, setSignupEmail] = useState('');
    const [signupUsername, setSignupUsername] = useState('');
    const [signupPassword, setSignupPassword] = useState('');
    const [signupConfirm, setSignupConfirm] = useState('');
    const [signupEmailError, setSignupEmailError] = useState('');
    const [signupUsernameError, setSignupUsernameError] = useState('');
    const [signupPasswordError, setSignupPasswordError] = useState('');
    const [signupConfirmError, setSignupConfirmError] = useState('');

    // After joining: the player's characters that could come along into the campaign's party.
    // Null until there's something to choose; see offerCharacterPick.
    const [pick, setPick] = useState(null);
    const [adding, setAdding] = useState(false);

    function finish() {
        navigate('/tavern');
        window.location.reload();
    }

    // Accepting an invite makes the user a member of the campaign's party, but none of their
    // characters. Offer to bring one along - added, not moved, so it stays in any party it's
    // already in (characters can belong to several). Returns false when there's nothing to offer.
    async function offerCharacterPick(user, campaignName) {
        try {
            const headers = { 'Authorization': 'Bearer ' + user.accessToken };
            const campaign = await fetch(`${process.env.REACT_APP_API_PROXY}/campaigns/findByInviteCode/${inviteCode}`, { headers })
                .then(r => (r.ok ? r.json() : null));
            const party = campaign?.party;
            if (!party?.id) return false;
            const chars = await fetch(`${process.env.REACT_APP_API_PROXY}/character/getAllCharactersForUser/${user.id}`, { headers })
                .then(r => (r.ok ? r.json() : []));
            const system = party.gameSystem ?? campaign.gameSystem ?? null;
            const eligible = (Array.isArray(chars) ? chars : [])
                // NPCs hang off a campaign, not a party; a character's rules must match the party's.
                .filter(c => c?.id && !c.campaignId)
                .filter(c => !system || !c.gameSystem || c.gameSystem === system)
                .filter(c => !(Array.isArray(c.parties) ? c.parties : []).some(p => p?.id === party.id));
            if (eligible.length === 0) return false;
            setPick({ user, party, campaignName: campaignName ?? campaign.campaignName, characters: eligible });
            return true;
        } catch (err) {
            console.error('Could not load characters to bring along', err);
            return false;
        }
    }

    async function bringCharacter(character) {
        if (adding) return;
        setAdding(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_PROXY}/character/${character.id}/parties/${pick.party.id}`,
                { method: 'POST', headers: { 'Authorization': 'Bearer ' + pick.user.accessToken } },
            );
            if (!res.ok) throw new Error((await res.text().catch(() => '')) || 'Could not add the character');
            await Swal.fire({
                timer: 2000, icon: 'success', showConfirmButton: false,
                title: `${character.characterName} joined ${pick.party.name}`,
            });
            finish();
        } catch (err) {
            Swal.fire({ timer: 3000, title: 'Error', text: err.message, icon: 'error', showConfirmButton: false });
            setAdding(false);
        }
    }

    // The server matches the invite to the signed-in user's own email, so nothing is sent but
    // the token. It answers with the invite; the campaign joined hangs off it.
    async function acceptInvite(accessToken) {
        const res = await fetch(
            `${process.env.REACT_APP_API_PROXY}/campaigns/acceptInvite/${inviteCode}`,
            {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + accessToken },
            }
        );
        if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(body || 'Failed to join campaign');
        }
        const invite = await res.json().catch(() => null);
        return invite?.campaign ?? null;
    }

    async function handleLogin(e) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);

        try {
            const authRes = await fetch(`${process.env.REACT_APP_API_PROXY}/auth/signin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: loginUsername, password: loginPassword }),
            }).then(r => r.json());

            if (!authRes.accessToken) {
                Swal.fire({ timer: 2500, title: 'Login Failed', text: authRes.message, icon: 'error', showConfirmButton: false });
                return;
            }

            sessionStorage.setItem('user', JSON.stringify(authRes));

            const campaign = await acceptInvite(authRes.accessToken);

            await Swal.fire({
                timer: 2000,
                title: 'Welcome back!',
                text: campaign?.campaignName ? `You have joined ${campaign.campaignName}.` : 'You have joined the campaign.',
                icon: 'success',
                showConfirmButton: false,
            });
            if (!(await offerCharacterPick(authRes, campaign?.campaignName))) finish();
        } catch (err) {
            Swal.fire({ timer: 2500, title: 'Error', text: err.message, icon: 'error', showConfirmButton: false });
        } finally {
            setSubmitting(false);
        }
    }

    const isEmailValid = (v) => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(v);
    const isPasswordValid = (v) =>
        /^(?=(.*[a-z]){3,})(?=(.*[A-Z]){2,})(?=(.*[0-9]){2,})(?=(.*[!@#$%^&*()\-_+.]){1,}).{8,}$/.test(v);

    async function handleSignup(e) {
        e.preventDefault();
        let valid = true;

        if (!signupUsername.trim()) { setSignupUsernameError('Username is required.'); valid = false; }
        else setSignupUsernameError('');

        if (!isEmailValid(signupEmail)) { setSignupEmailError('Invalid email format.'); valid = false; }
        else setSignupEmailError('');

        if (!isPasswordValid(signupPassword)) { setSignupPasswordError('Password does not meet requirements.'); valid = false; }
        else setSignupPasswordError('');

        if (signupConfirm !== signupPassword) { setSignupConfirmError('Passwords do not match.'); valid = false; }
        else setSignupConfirmError('');

        if (!valid || submitting) return;
        setSubmitting(true);

        try {
            const signupHttpRes = await fetch(`${process.env.REACT_APP_API_PROXY}/auth/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: signupUsername, password: signupPassword, email: signupEmail }),
            });
            const regRes = await signupHttpRes.json();

            if (!signupHttpRes.ok || regRes.message?.toLowerCase().includes('error')) {
                Swal.fire({ timer: 2500, title: 'Registration Failed', text: regRes.message || 'Registration failed', icon: 'error', showConfirmButton: false });
                return;
            }

            // Auto-login after signup
            const authRes = await fetch(`${process.env.REACT_APP_API_PROXY}/auth/signin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: signupUsername, password: signupPassword }),
            }).then(r => r.json());

            if (!authRes.accessToken) {
                await Swal.fire({ timer: 2000, title: 'Account created!', text: 'Please log in to join the campaign.', icon: 'success', showConfirmButton: false });
                setTab(TAB_LOGIN);
                return;
            }

            sessionStorage.setItem('user', JSON.stringify(authRes));

            const campaign = await acceptInvite(authRes.accessToken);

            await Swal.fire({
                timer: 2000,
                title: 'Welcome to DropWyrm!',
                text: campaign?.campaignName
                    ? `Your account is ready and you have joined ${campaign.campaignName}.`
                    : 'Your account is ready and you have joined the campaign.',
                icon: 'success',
                showConfirmButton: false,
            });
            finish();
        } catch (err) {
            Swal.fire({ timer: 2500, title: 'Error', text: err.message, icon: 'error', showConfirmButton: false });
        } finally {
            setSubmitting(false);
        }
    }

    const tabStyle = (active) => ({
        flex: 1,
        padding: '10px 0',
        background: active ? 'rgba(var(--th-6c4f9b-rgb), 0.85)' : 'transparent',
        border: 'none',
        borderRadius: '8px 8px 0 0',
        color: '#fff',
        cursor: 'pointer',
        fontWeight: active ? 700 : 400,
        fontSize: '15px',
        outline: 'none',
        transition: 'background 0.2s',
    });

    return (
        <div className="login" style={loginBgStyle}>
            <div className="wrapper" style={{ width: '460px', paddingTop: '24px' }}>
                <img src={ddLogo} alt="DD_Logo" className="brand-logo brand-logo--lg" />

                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.75)', fontSize: '14px', margin: '16px 0 8px' }}>
                    You've been invited to join a campaign. Sign in or create an account to continue.
                </p>

                {pick ? (
                    <div>
                        <p style={{ textAlign: 'center', color: '#fff', fontSize: '16px', fontWeight: 700, margin: '8px 0 4px' }}>
                            Join with which character?
                        </p>
                        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '13px', margin: '0 0 12px' }}>
                            They'll be added to {pick.party.name}{pick.campaignName ? ` for ${pick.campaignName}` : ''} and
                            stay in any party they're already in.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto' }}>
                            {pick.characters.map(c => {
                                const others = (Array.isArray(c.parties) ? c.parties : []).map(p => p?.name).filter(Boolean);
                                return (
                                    <button key={c.id} type="button" disabled={adding} onClick={() => bringCharacter(c)}
                                        style={{
                                            textAlign: 'left', padding: '10px 14px', borderRadius: '8px', cursor: adding ? 'default' : 'pointer',
                                            background: 'rgba(var(--th-6c4f9b-rgb), 0.35)', border: '1px solid rgba(var(--lm-bd-th-c9a0f0-rgb), 0.5)', color: '#fff',
                                            height: 'auto', fontSize: '15px', fontWeight: 600,
                                        }}>
                                        {c.characterName || 'Unnamed character'}
                                        {others.length > 0 && (
                                            <span style={{ display: 'block', fontSize: '12px', fontWeight: 400, color: 'rgba(255,255,255,0.65)' }}>
                                                Also in {others.join(', ')}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="register-link">
                            <p>
                                <button type="button" onClick={finish} disabled={adding}
                                    style={{
                                        background: 'none', border: 'none', color: '#fff',
                                        cursor: 'pointer', fontWeight: 600, textDecoration: 'underline',
                                        padding: 0, fontSize: 'inherit',
                                    }}>
                                    Skip for now
                                </button>
                            </p>
                        </div>
                    </div>
                ) : (<>
                {/* Tabs */}
                <div style={{ display: 'flex', marginBottom: '0', borderBottom: '2px solid rgba(var(--lm-veil-rgb),0.2)' }}>
                    <button style={tabStyle(tab === TAB_LOGIN)} onClick={() => setTab(TAB_LOGIN)}>
                        Sign In
                    </button>
                    <button style={tabStyle(tab === TAB_SIGNUP)} onClick={() => setTab(TAB_SIGNUP)}>
                        Create Account
                    </button>
                </div>

                {/* Login form */}
                {tab === TAB_LOGIN && (
                    <form onSubmit={handleLogin}>
                        <div className="input-box">
                            <input
                                type="text"
                                placeholder="Username"
                                value={loginUsername}
                                onChange={e => setLoginUsername(e.target.value)}
                                required
                            />
                            <GenreIcon name="username" className="icon"/>
                        </div>
                        <div className="input-box">
                            <input
                                type="password"
                                placeholder="Password"
                                value={loginPassword}
                                onChange={e => setLoginPassword(e.target.value)}
                                required
                            />
                            <RiLockPasswordFill className="icon" />
                        </div>
                        <button type="submit" disabled={submitting}>
                            {submitting ? 'Signing in…' : 'Sign In & Join Campaign'}
                        </button>
                        <div className="register-link">
                            <p>
                                No account?{' '}
                                <button
                                    type="button"
                                    onClick={() => setTab(TAB_SIGNUP)}
                                    style={{
                                        background: 'none', border: 'none', color: '#fff',
                                        cursor: 'pointer', fontWeight: 600, textDecoration: 'underline',
                                        padding: 0, fontSize: 'inherit',
                                    }}
                                >
                                    Create one
                                </button>
                            </p>
                        </div>
                    </form>
                )}

                {/* Signup form */}
                {tab === TAB_SIGNUP && (
                    <form onSubmit={handleSignup}>
                        <div className="input-box">
                            <input
                                type="text"
                                placeholder="Email"
                                value={signupEmail}
                                onChange={e => setSignupEmail(e.target.value)}
                                onBlur={() => {
                                    if (!isEmailValid(signupEmail)) setSignupEmailError('Invalid email format.');
                                    else setSignupEmailError('');
                                }}
                            />
                            <MdAlternateEmail className="icon" />
                            {signupEmailError && <p>{signupEmailError}</p>}
                        </div>
                        <div className="input-box">
                            <input
                                type="text"
                                placeholder="Username"
                                value={signupUsername}
                                onChange={e => setSignupUsername(e.target.value)}
                                onBlur={() => {
                                    if (!signupUsername.trim()) setSignupUsernameError('Username is required.');
                                    else setSignupUsernameError('');
                                }}
                            />
                            <GenreIcon name="username" className="icon"/>
                            {signupUsernameError && <p>{signupUsernameError}</p>}
                        </div>
                        <div className="input-box">
                            <input
                                type="password"
                                placeholder="Password"
                                value={signupPassword}
                                onChange={e => setSignupPassword(e.target.value)}
                                onBlur={() => {
                                    if (!isPasswordValid(signupPassword)) setSignupPasswordError('Password does not meet requirements.');
                                    else setSignupPasswordError('');
                                }}
                            />
                            <RiLockPasswordFill className="icon" />
                            {signupPasswordError && (
                                <p title="Min 8 chars, 3 lowercase, 2 uppercase, 2 digits, 1 special char">
                                    {signupPasswordError}
                                </p>
                            )}
                        </div>
                        <div className="input-box">
                            <input
                                type="password"
                                placeholder="Confirm Password"
                                value={signupConfirm}
                                onChange={e => setSignupConfirm(e.target.value)}
                                onBlur={() => {
                                    if (signupConfirm !== signupPassword) setSignupConfirmError('Passwords do not match.');
                                    else setSignupConfirmError('');
                                }}
                            />
                            <RiLockPasswordFill className="icon" />
                            {signupConfirmError && <p>{signupConfirmError}</p>}
                        </div>
                        <button type="submit" disabled={submitting}>
                            {submitting ? 'Creating account…' : 'Create Account & Join Campaign'}
                        </button>
                        <div className="register-link">
                            <p>
                                Already have an account?{' '}
                                <button
                                    type="button"
                                    onClick={() => setTab(TAB_LOGIN)}
                                    style={{
                                        background: 'none', border: 'none', color: '#fff',
                                        cursor: 'pointer', fontWeight: 600, textDecoration: 'underline',
                                        padding: 0, fontSize: 'inherit',
                                    }}
                                >
                                    Sign in
                                </button>
                            </p>
                        </div>
                    </form>
                )}
                </>)}
            </div>

            {/* Studio logo */}
            <div style={{
                position: 'absolute', bottom: '10px', right: '10px',
                width: '128px', height: '128px', borderRadius: '12px',
            }}>
                <a href="http://www.virtualrealmsstudios.com" target="_blank" rel="noreferrer">
                    <VrPortalLogo width="128px" height="128px" alt="virtualStudiosLogo" />
                </a>
            </div>

            <div className="copyright-footer">
                &copy; {new Date().getFullYear()} Virtual Realms Studios, LLC. All rights reserved.
            </div>
        </div>
    );
}

export default JoinPage;